import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PageContainer from '../../../components/layout/PageContainer';
import PageHeader from '../../../components/layout/PageHeader';
import ContentCard from '../../../components/layout/ContentCard';
import Table from '../../../components/data/Table';
import Pagination from '../../../components/data/Pagination';
import SearchFilter from '../../../components/data/SearchFilter';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import { translate } from '../../../utils/Common';
import { verifyAccessViewPermissions } from '../../../config/permissions';
import useCompanies from './useCompanies';
import CreateCompanyModal from './components/CreateCompanyModal';
import BtnSync from '../../../components/BtnSync';
import styles from './Companies.module.css';
import * as FaIcons from 'react-icons/fa';
import { HiOfficeBuilding } from 'react-icons/hi';

const Companies = () => {
  const history = useHistory();
  const location = useLocation();
  const permissionsPath = "/dashboard/companies";
  const canRead = verifyAccessViewPermissions(["read"], { path: permissionsPath || location.pathname });
  const canCreate = verifyAccessViewPermissions(["create"], { path: permissionsPath || location.pathname });
  const canSearch = verifyAccessViewPermissions(["search"], { path: permissionsPath || location.pathname });
  const canViewDetails = verifyAccessViewPermissions(["view-details"], { path: permissionsPath || location.pathname });
  // Sincronizar compañía: la visibilidad la decide la acción `sync` asignada al rol en el
  // catálogo de permisos del módulo /dashboard/companies (el endpoint solo existe en el api de USA).
  const canSync = verifyAccessViewPermissions(["sync"], { path: permissionsPath || location.pathname });
  const {
    companies,
    loading,
    error,
    pagination,
    filters,
    selectedField,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handlePageChange,
    handleSort,
    handleItemsPerPageChange,
    handleCreateCompany,
  } = useCompanies();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [localSelectedField, setLocalSelectedField] = useState(selectedField || 'name');

  // Sincronizar localSelectedField con selectedField del hook
  React.useEffect(() => {
    setLocalSelectedField(selectedField || 'name');
  }, [selectedField]);

  const handleOpenCreateModal = () => {
    if (!canCreate) return;
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateSuccess = async (companyData) => {
    const result = await handleCreateCompany(companyData);
    if (result.success) {
      handleCloseCreateModal();
    }
    return result;
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'warning';
      case 'Archived':
        return 'archived';
      default:
        return 'neutral';
    }
  };

  // Definir opciones para el selector de campo en modo unificado
  const fieldOptions = useMemo(() => [
    {
      value: 'name',
      label: translate('dashboard.companies.columns.companyName'),
    },
    {
      value: 'companyCode',
      label: translate('dashboard.companies.columns.companyCode'),
    },
    {
      value: 'email',
      label: translate('dashboard.companies.columns.contactName') + ' (Email)',
    },
    {
      value: 'contactName',
      label: translate('dashboard.companies.columns.contactName'),
    },
  ], []);

  // Opciones para el selector de status (con iconos)
  const statusOptions = useMemo(() => [
    {
      value: 'all',
      label: translate('dashboard.clients.filters.allStatus') || 'Todos los estados',
      icon: <FaIcons.FaList size={16} />,
      showLabel: true,
    },
    {
      value: 'Active',
      label: translate('dashboard.companies.status.active'),
      icon: <FaIcons.FaCheckCircle size={16} />,
      showLabel: true,
    },
    {
      value: 'Inactive',
      label: translate('dashboard.companies.status.inactive'),
      icon: <FaIcons.FaTimesCircle size={16} />,
      showLabel: true,
    },
  ], []);

  // Sincronizar selectedStatus con el filtro actual
  React.useEffect(() => {
    if (filters.status) {
      setSelectedStatus(filters.status);
    } else {
      setSelectedStatus('all');
    }
  }, [filters.status]);

  const columns = [
    { 
      key: 'NombreEmpresa', 
      label: translate('dashboard.companies.columns.companyName'), 
      sortable: true,
      width: '200px',
      render: (row) => (
        <span 
          title={row.NombreEmpresa || ''}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px'
          }}
        >
          {row.NombreEmpresa || '-'}
        </span>
      )
    },
    { 
      key: 'CodUser', 
      label: translate('dashboard.companies.columns.companyCode'), 
      sortable: true,
      width: '150px'
    },
    { 
      key: 'Contact', 
      label: translate('dashboard.companies.columns.contactName'),
      width: '180px',
      render: (row) => {
        const fullName = [row.PrimerNombre, row.PrimerApellido].filter(Boolean).join(' ');
        return (
          <span 
            title={fullName || ''}
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '180px'
            }}
          >
            {fullName || '-'}
          </span>
        );
      }
    },
    { 
      key: 'Activo', 
      label: translate('global.status'),
      width: '100px',
      render: (row) => (
        <Badge variant={getStatusVariant(row.Activo ? 'Active' : 'Inactive')}>
          {row.Activo ? translate('dashboard.companies.status.active') : translate('dashboard.companies.status.inactive')}
        </Badge>
      )
    }
  ];

  // Acciones por fila: la columna solo se agrega cuando el rol tiene la acción `sync`.
  if (canSync) {
    columns.push({
      key: 'actions',
      label: '',
      sortable: false,
      width: '80px',
      render: (row) => {
        const companyCode = row?.companyCode || row?.boxitCompanyCode || row?.CodUser || row?.id;
        if (!companyCode) return null;

        return (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span title={translate('dashboard.companies.tooltips.sync')}>
              <BtnSync
                id={`btn-sync-company-${companyCode}`}
                mode="company"
                companyCode={companyCode}
                size="sm"
                variant="btn"
              />
            </span>
          </div>
        );
      },
    });
  }

  const handleRowClick = (row) => {
    if (!canViewDetails) return;
    const companyId = row?.companyCode || row?.boxitCompanyCode || row?.CodUser || row?.id;
    if (companyId) {
      history.push(`/dashboard/companies/${companyId}`);
    }
  };

  if (!canRead) {
    return (
      <PageContainer>
        <PageHeader
          title={translate('global.companies')}
          subtitle={translate('dashboard.companies.subtitle')}
          icon={<HiOfficeBuilding size={28} />}
          divider={false}
        />
        <ContentCard padding="md">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(107,114,128,0.12)",
                color: "#374151",
                flex: "0 0 auto",
              }}
              aria-hidden
            >
              <FaIcons.FaLock size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {translate("dashboard.configuration.noPermissionTitle")}
              </div>
              <div style={{ marginTop: 4, color: "#6B7280" }}>
                {translate("dashboard.clients.formSearch.NOT_ACCESS")}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" type="button" onClick={() => history.push("/dashboard")}>
                  {translate("global.goBack")}
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={translate('global.companies')}
        subtitle={translate('dashboard.companies.subtitle')}
        icon={<HiOfficeBuilding size={28} />}
        actions={
          canCreate && (
            <Button 
              variant="primary" 
              onClick={handleOpenCreateModal}
              leftIcon={<FaIcons.FaPlus />}
            >
              {translate('dashboard.companies.actions.create')}
            </Button>
          )
        }
      />
      {canSearch && (
        <SearchFilter
          placeholder={translate('global.search') + ' ...'}
          onSearch={handleSearch}
          onFilter={handleFilter}
          activeFilters={filters}
          loading={loading}
          unifiedMode={true}
          selectedField={localSelectedField}
          onFieldChange={(field) => {
            setLocalSelectedField(field);
            // Solo cambiar el campo seleccionado, no hacer búsqueda automática
          }}
          fieldOptions={fieldOptions}
          statusFilter={true}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          statusOptions={statusOptions}
        />
      )}
        
        {error && (
          <ContentCard>
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#fee', 
              color: '#c33', 
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <strong>{translate('global.error')}:</strong> {error}
            </div>
          </ContentCard>
        )}

        <Table
          columns={columns}
          data={companies}
          loading={loading}
          onSort={handleSort}
          onRowClick={handleRowClick}
          emptyMessage={translate('dashboard.companies.empty')}
          hoverable
        />

        {!loading && companies.length > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}

      <CreateCompanyModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onCreateSuccess={handleCreateSuccess}
      />
    </PageContainer>
  );
};

export default Companies;
